/**
 * ProgramDetailsPanel
 *
 * Pure, compositional component for displaying assistance program details (CFDA/SAL)
 * using the DataSheet component for spreadsheet-style key-value display.
 *
 * Shows for assistance awards: grants, direct payments, loans, insurance.
 * Displays CFDA program information from the award's classification data.
 */
import * as React from 'react'

import { ExternalLink, GraduationCap } from 'lucide-react'

import { DataSheet } from '@/components/opportunity/common'

// ============================================================================
// Types
// ============================================================================

interface ProgramData {
  /** CFDA/Assistance Listing number (e.g., "10.001") */
  cfdaNumber?: string
  /** CFDA program title */
  cfdaTitle?: string
  /** Additional CFDA numbers if award spans multiple programs */
  cfdaNumbers?: string[]
  /** Program objectives (from references_cfda if available) */
  objectives?: string
  /** Applicant eligibility requirements */
  applicantEligibility?: string
  /** Program website URL */
  websiteUrl?: string
  /** Types of assistance provided */
  typesOfAssistance?: string
}

// ============================================================================
// Utilities
// ============================================================================

/**
 * Check if assistance award category (shows program details)
 */
function isAssistanceCategory(category: string | null | undefined): boolean {
  if (!category) return false
  const normalized = category.toLowerCase().trim()
  return ['grant', 'direct payment', 'loans', 'insurance', 'other'].includes(
    normalized,
  )
}

/**
 * Check if there's meaningful program data to display
 */
function hasProgramData(data: ProgramData | null | undefined): boolean {
  if (!data) return false
  return !!(
    data.cfdaNumber ||
    data.cfdaTitle ||
    (data.cfdaNumbers && data.cfdaNumbers.length > 0)
  )
}

/**
 * Build the SAM.gov Assistance Listing URL for a CFDA number
 */
function buildAssistanceListingUrl(cfdaNumber: string): string {
  // SAM.gov Assistance Listing uses the CFDA number in the URL
  // Format: https://sam.gov/fal/[number-without-periods]/view
  // Example: 10.001 -> https://sam.gov/fal/10001/view
  const cleanNumber = cfdaNumber.replace('.', '')
  return `https://sam.gov/fal/${cleanNumber}/view`
}

// ============================================================================
// Row Data Builders
// ============================================================================

interface RowData {
  label: string
  value: React.ReactNode
  highlight?: boolean
}

function buildProgramRows(data: ProgramData): RowData[] {
  const rows: RowData[] = []

  // CFDA Number with link
  if (data.cfdaNumber) {
    const samUrl = buildAssistanceListingUrl(data.cfdaNumber)
    rows.push({
      label: 'Assistance Listing',
      value: (
        <a
          href={samUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-accent-600 hover:text-accent-700 hover:underline dark:text-accent-400 dark:hover:text-accent-300"
        >
          {data.cfdaNumber}
          <ExternalLink className="h-3 w-3" />
        </a>
      ),
      highlight: true,
    })
  }

  // CFDA Program Title
  if (data.cfdaTitle) {
    rows.push({
      label: 'Program Title',
      value: data.cfdaTitle,
    })
  }

  // Additional CFDA numbers (if award spans multiple programs)
  if (data.cfdaNumbers && data.cfdaNumbers.length > 1) {
    // Filter out the primary CFDA number if it's already shown
    const additionalNumbers = data.cfdaNumbers.filter(
      (n) => n !== data.cfdaNumber,
    )
    if (additionalNumbers.length > 0) {
      rows.push({
        label: 'Additional Programs',
        value: additionalNumbers.join(', '),
      })
    }
  }

  // Types of Assistance
  if (data.typesOfAssistance) {
    rows.push({
      label: 'Types of Assistance',
      value: data.typesOfAssistance,
    })
  }

  // Program Objectives (if available from enhanced data)
  if (data.objectives) {
    rows.push({
      label: 'Program Objectives',
      value: data.objectives,
    })
  }

  // Eligibility (if available from enhanced data)
  if (data.applicantEligibility) {
    rows.push({
      label: 'Applicant Eligibility',
      value: data.applicantEligibility,
    })
  }

  // Program Website (if different from SAM.gov link)
  if (data.websiteUrl) {
    rows.push({
      label: 'Program Website',
      value: (
        <a
          href={data.websiteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-accent-600 hover:text-accent-700 hover:underline dark:text-accent-400 dark:hover:text-accent-300"
        >
          Visit Website
          <ExternalLink className="h-3 w-3" />
        </a>
      ),
    })
  }

  return rows
}

// ============================================================================
// Main Component
// ============================================================================

interface ProgramDetailsPanelProps {
  /** Award category to determine if panel should show */
  category: string | null | undefined
  /** Program data from award classification and enhanced CFDA data */
  data: ProgramData
}

export const ProgramDetailsPanel: React.FC<ProgramDetailsPanelProps> = ({
  category,
  data,
}) => {
  // Only show for assistance awards
  if (!isAssistanceCategory(category)) return null

  // Only show if there's program data
  if (!hasProgramData(data)) return null

  const rows = buildProgramRows(data)

  if (rows.length === 0) return null

  return (
    <DataSheet title="Program Details" icon={<GraduationCap />}>
      <DataSheet.Section title="Assistance Listing (CFDA)">
        {rows.map((row, index) => (
          <DataSheet.Row
            key={row.label + index}
            label={row.label}
            value={row.value}
            highlight={row.highlight}
          />
        ))}
      </DataSheet.Section>
    </DataSheet>
  )
}

export { hasProgramData, isAssistanceCategory }
export type { ProgramData }
